// API Configuration
// REACT_APP_BASE_URL: Backend API base URL (with /api suffix)
// REACT_APP_API_URL: Public/Frontend base URL (for assets, public resources)
//
// Development:
//   REACT_APP_BASE_URL=http://localhost:3000/api
//   REACT_APP_API_URL=http://localhost:3001
//
// Staging:
//   REACT_APP_BASE_URL=https://cms.remotebirdy.xyz/api
//   REACT_APP_API_URL=https://remotebirdy.xyz/
//
// Production:
//   REACT_APP_BASE_URL=https://cms.remotebirdy.com/api
//   REACT_APP_API_URL=https://remotebirdy.com/

const getApiBaseUrl = () => {
  const baseUrl = process.env.REACT_APP_BASE_URL;
  
  // During build, warn if BASE_URL is not set (only allow localhost in development)
  if (!baseUrl) {
    if (process.env.REACT_APP_ENV === 'production') {
      console.error('⚠️ REACT_APP_BASE_URL is not set! This is required for production builds.');
      return 'https://cms.remotebirdy.com/api';
    }
    if (process.env.REACT_APP_ENV === 'staging') {
      console.warn('⚠️ REACT_APP_BASE_URL not set, using staging fallback. Create a .env file with REACT_APP_BASE_URL for production.');
      return 'https://cms.remotebirdy.xyz/api';
    }
    // Only allow localhost fallback in development mode
    if (process.env.REACT_APP_ENV === 'development') {
      console.warn('⚠️ REACT_APP_BASE_URL not set, using localhost fallback. Create a .env file with REACT_APP_BASE_URL for production.');
      return 'http://localhost:8000/api';
    }
  }
  
  // Ensure BASE_URL ends with /api if it doesn't already
  if (baseUrl && !baseUrl.endsWith('/api')) {
    return baseUrl.endsWith('/') ? `${baseUrl}api` : `${baseUrl}/api`;
  }
  
  return baseUrl;
};

const getPublicApiUrl = () => {
  const apiUrl = process.env.REACT_APP_API_URL;
  
  // If not set, return empty string (optional variable)
  if (!apiUrl) {
    return '';
  }
  
  // Ensure it ends with / if it's not empty
  return apiUrl.endsWith('/') ? apiUrl : `${apiUrl}/`;
};

/**
 * Get Pusher configuration
 * Tries multiple sources: .env, hardcoded fallback, or fetches from backend
 */
const getPusherConfig = () => {
  // Try .env first
  let pusherKey = process.env.REACT_APP_PUSHER_KEY;
  let pusherCluster = process.env.REACT_APP_PUSHER_CLUSTER;

  // If not in .env, try hardcoded values (for development)
  // You can update these directly in this file
  if (!pusherKey) {
    pusherKey = '8eaccf994973a7092814'; // Default Pusher key - update this if needed
  }

  if (!pusherCluster) {
    pusherCluster = 'mt1'; // Default cluster - update this if needed
  }

  return {
    key: pusherKey,
    cluster: pusherCluster,
    enabled: !!(pusherKey && pusherKey !== 'your-pusher-key' && pusherKey.trim() !== ''),
  };
};

export const API_CONFIG = {
  // Base URL for backend API calls - uses REACT_APP_BASE_URL environment variable
  // Required for production builds
  BASE_URL: getApiBaseUrl(),
  
  // Public/Frontend base URL - uses REACT_APP_API_URL environment variable
  // Optional, used for public resources, asset URLs, etc.
  PUBLIC_URL: getPublicApiUrl(),
  
  // Timeout for API requests (in milliseconds)
  TIMEOUT: 10000,
  
  // Default headers
  DEFAULT_HEADERS: {
    'Content-Type': 'application/json',
    'Accept': 'application/json',
  },

  // Pusher configuration for real-time notifications
  PUSHER: getPusherConfig(),
};

// Helper function to get the full API URL (for backend API calls)
export const getApiUrl = (endpoint) => {
  return `${API_CONFIG.BASE_URL}${endpoint}`;
};

// Helper function to get the base API URL (backend)
export const getBaseApiUrl = () => {
  return API_CONFIG.BASE_URL;
};

// Helper function to get the public URL (for assets, public resources)
export const getPublicUrl = () => {
  return API_CONFIG.PUBLIC_URL;
};

// Helper function to get storage URL for uploaded files
export const getStorageUrl = (filePath) => {
  if (!filePath) {
    return '';
  }
  
  // If the path is already a full URL, return it as is
  if (filePath.startsWith('http://') || filePath.startsWith('https://')) {
    return filePath;
  }
  
  // Get the base URL (remove /api if present)
  const baseUrl = API_CONFIG.PUBLIC_URL || API_CONFIG.BASE_URL.replace('/api', '');
  
  // Remove leading slash from filePath if present
  const cleanPath = filePath.startsWith('/') ? filePath.substring(1) : filePath;
  
  // Construct the full storage URL
  // If filePath already includes 'storage/', use it as is, otherwise prepend it
  if (cleanPath.startsWith('storage/')) {
    return `${baseUrl}${baseUrl.endsWith('/') ? '' : '/'}${cleanPath}`;
  } else {
    return `${baseUrl}${baseUrl.endsWith('/') ? '' : '/'}storage/${cleanPath}`;
  }
};
